import { Router, ScreenBase } from "@frui.ts/screens";
import { action, computed, observable, reaction } from "mobx";
import AppRouter from "services/appRouter";
import LocalizationService from "services/localizationService";
import RecapViewModel from "./recapViewModel";
import ChargeTypeViewModel from "./chargeTypeViewModel";
import { interfaces } from "inversify";
import ContactViewModelCommon from "viewModels/common/contactViewModel";
import ChargeOrderContactItem from "models/chargeOrderContactItem";
import ChargeOrderService from "services/chargeOrderService";
import { attachAutomaticBlurValidator } from "utils/validationPatch";
import { ManualEntityValidator, validate } from "@frui.ts/validation";
import { scrollToElement } from "utils/scrollTo";
import NotificationPreferences, {
  EmailConfirmationValidationRules,
  EmailNotificationValidationRules,
  EmailValidationRules,
  NotificationValidationRules,
} from "models/notificationPreferences";
export const ContactViewNavigationName = "contact";

@Router.registerRoute({
  name: Router.Self,
  route: ContactViewNavigationName,
})
export default class ContactViewModel extends ScreenBase {
  public contactViewModel: ContactViewModelCommon;

  private unsubscriptions: Array<() => void> = [];
  protected manualValidator = new ManualEntityValidator<NotificationPreferences>(false);

  @observable formElement?: HTMLFormElement;

  constructor(
    public chargeOrderContactItem: ChargeOrderContactItem,
    public localizationService: LocalizationService,
    private router: AppRouter,
    private chargeOrderService: ChargeOrderService,
    private contactViewModelFactory: ReturnType<typeof ContactViewModelCommon.Factory>
  ) {
    super();
    this.navigationName = ContactViewNavigationName;
    this.nameValue = "eshop.title";
    this.contactViewModel = this.contactViewModelFactory(this.chargeOrderContactItem, true);
    attachAutomaticBlurValidator(this.notificationPreferences, {
      ...EmailValidationRules,
      ...EmailConfirmationValidationRules,
      ...EmailNotificationValidationRules,
      ...NotificationValidationRules,
    });
  }

  protected onInitialize(): Promise<any> | void {
    this.unsubscriptions.push(
      reaction(() => this.notificationPreferences._isNotificationEmailSame, this.onSameNotificationEmailChanged),
      reaction(() => this.notificationPreferences.emailAddress, this.onEmailAdressChanged),
      reaction(() => this.notificationPreferences._emailAddressNotification, this.onEmailAddressNotification),
      reaction(() => this.notificationPreferences.phoneNumber, this.onPhoneNumberChanged)
    );
    return super.onInitialize();
  }

  protected async onActivate() {
    if (this.stepper) {
      scrollToElement(this.stepper);
    }
    this.checkStoredNotificationPreferences();
    return super.onActivate();
  }

  protected onDeactivate(isClosing: boolean) {
    if (isClosing) {
      this.unsubscriptions.forEach(unsubscribe => unsubscribe());
      this.unsubscriptions = [];
    }
    return super.onDeactivate(isClosing);
  }

  @computed
  get notificationPreferences() {
    return this.chargeOrderContactItem.notificationPreferences;
  }

  @computed
  get isDisabled() {
    return (
      !!this.chargeOrderContactItem.notificationPreferences.emailAddress &&
      !!this.chargeOrderContactItem.notificationPreferences._emailConfirmation
    );
  }

  @computed
  get isSingleChargeType() {
    return this.chargeOrderService.chargeOrderItem._singleChargeType;
  }

  @computed
  get stepper() {
    return window.document.getElementById("stepper");
  }

  @action.bound
  onSameNotificationEmailChanged() {
    if (this.notificationPreferences._isNotificationEmailSame) {
      this.notificationPreferences._emailAddressNotification = this.notificationPreferences.emailAddress;
      this.notificationPreferences.emailNotificationEnabled = true;
      return;
    }
    this.notificationPreferences._emailAddressNotification = undefined;
  }

  @action.bound
  onEmailAdressChanged() {
    if (!this.notificationPreferences.emailAddress) {
      this.notificationPreferences._isNotificationEmailSame = false;
    }
    if (this.notificationPreferences._isNotificationEmailSame) {
      this.notificationPreferences._emailAddressNotification = this.notificationPreferences.emailAddress;
      return;
    }
  }

  @action.bound
  onEmailAddressNotification() {
    const isEmailSet =
      this.notificationPreferences._emailAddressNotification !== undefined &&
      this.notificationPreferences._emailAddressNotification.length > 0;
    this.notificationPreferences.emailNotificationEnabled = isEmailSet;
    if (this.notificationPreferences._isNotificationEmailSame) {
      this.notificationPreferences._emailAddressNotification = this.notificationPreferences.emailAddress;
      return;
    }
  }

  @action.bound
  onPhoneNumberChanged() {
    const isPhoneNumberSet =
      this.notificationPreferences.phoneNumber !== undefined && this.notificationPreferences.phoneNumber.length > 0;
    this.notificationPreferences.smsNotificationEnabled = isPhoneNumberSet;
    if (this.notificationPreferences.phoneNumber) {
      this.contactViewModel.phoneNumberChanged = true;
      return;
    }
  }
  @action.bound
  private checkStoredNotificationPreferences() {
    const chargeOrder = this.chargeOrderService.chargeOrder;

    if (chargeOrder.emailAddress && this.isSingleChargeType === false) {
      this.chargeOrderContactItem.notificationPreferences.emailAddress = chargeOrder.emailAddress;
      this.chargeOrderContactItem.notificationPreferences._emailAddressNotification =
        chargeOrder.notificationPreferences._emailAddressNotification;
      this.chargeOrderContactItem.notificationPreferences._notificationEnabled =
        chargeOrder.notificationPreferences._notificationEnabled;
      this.chargeOrderContactItem.notificationPreferences._emailConfirmation =
        chargeOrder.notificationPreferences._emailConfirmation;
      this.chargeOrderContactItem.notificationPreferences.emailNotificationEnabled =
        chargeOrder.notificationPreferences.emailNotificationEnabled;
      this.chargeOrderContactItem.notificationPreferences._isNotificationEmailSame =
        chargeOrder.notificationPreferences._isNotificationEmailSame;
      this.chargeOrderContactItem.notificationPreferences.smsNotificationEnabled =
        chargeOrder.notificationPreferences.smsNotificationEnabled;
      this.chargeOrderContactItem.notificationPreferences.phoneNumber = chargeOrder.notificationPreferences.phoneNumber;
      this.chargeOrderService.persistChargeOrder();

      return;
    }
    if (chargeOrder.notificationPreferences && chargeOrder.proposedPurchaseRequestId && !this.isSingleChargeType) {
      this.chargeOrderContactItem.notificationPreferences.emailAddress = chargeOrder.emailAddress;
      this.chargeOrderContactItem.notificationPreferences._emailAddressNotification =
        chargeOrder.notificationPreferences._emailAddressNotification;
      this.chargeOrderContactItem.notificationPreferences._notificationEnabled =
        chargeOrder.notificationPreferences._notificationEnabled;
      this.chargeOrderContactItem.notificationPreferences._emailConfirmation =
        chargeOrder.notificationPreferences._emailConfirmation;
      this.chargeOrderContactItem.notificationPreferences.emailNotificationEnabled =
        chargeOrder.notificationPreferences.emailNotificationEnabled;
      this.chargeOrderContactItem.notificationPreferences._isNotificationEmailSame =
        chargeOrder.notificationPreferences._isNotificationEmailSame;
      this.chargeOrderContactItem.notificationPreferences.smsNotificationEnabled =
        chargeOrder.notificationPreferences.smsNotificationEnabled;
      this.chargeOrderContactItem.notificationPreferences.phoneNumber = chargeOrder.notificationPreferences.phoneNumber;
      this.chargeOrderService.persistChargeOrder();
      return;
    }
    if (chargeOrder.emailAddress && this.isSingleChargeType) {
      this.chargeOrderContactItem.notificationPreferences = chargeOrder.notificationPreferences =
        this.chargeOrderService.resetNotificationPreferences(this.chargeOrderContactItem.notificationPreferences);
      this.chargeOrderContactItem.notificationPreferences.emailAddress = chargeOrder.emailAddress;
      this.chargeOrderContactItem.notificationPreferences._emailConfirmation = chargeOrder.emailAddress;
      this.chargeOrderService.persistStoredChargeOrder(chargeOrder);
      return;
    } else {
      this.chargeOrderContactItem.notificationPreferences = chargeOrder.notificationPreferences =
        this.chargeOrderService.resetNotificationPreferences(this.chargeOrderContactItem.notificationPreferences);
      this.chargeOrderService.persistStoredChargeOrder(chargeOrder);
    }
  }

  @action.bound
  private validateAll() {
    return validate(this.notificationPreferences);
  }

  @action.bound
  private updateChargeOrderItem() {
    const chargeOrder = this.chargeOrderService.chargeOrder;
    if (this.notificationPreferences.emailAddress) {
      chargeOrder.emailAddress = this.notificationPreferences.emailAddress;
    }
    if (this.notificationPreferences && this.isSingleChargeType === false) {
      chargeOrder.notificationPreferences = this.notificationPreferences;
    }
    this.chargeOrderService.persistStoredChargeOrder(chargeOrder);
  }

  @action.bound
  public onContinue() {
    if (!this.validateAll()) {
      return;
    }
    this.updateChargeOrderItem();
    this.router.navigate(RecapViewModel);
  }

  @action.bound
  public onBack() {
    this.updateChargeOrderItem();
    this.chargeOrderService.persistStoredChargeOrder(this.chargeOrderService.chargeOrder);
    this.router.navigate(ChargeTypeViewModel);
  }

  static Factory({ container }: interfaces.Context) {
    return (chargeOrderContactItem: ChargeOrderContactItem) =>
      new ContactViewModel(
        chargeOrderContactItem,
        container.get(LocalizationService),
        container.get(AppRouter),
        container.get(ChargeOrderService),
        container.get(ContactViewModelCommon.Factory)
      );
  }
}
